import { useState, useEffect } from "react";
import NextLink from "next/link";
import { useSession } from "next-auth/react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  Box,
  Link,
  Text,
  Flex,
  Center,
} from "@chakra-ui/react";
import { ImStatsBars } from "react-icons/im";
import toast from "react-hot-toast";
import PuffLoader from "react-spinners/PuffLoader";
import Thread from "components/tweets/masonry/thread";
import { useFirebaseUser } from "utils/useFirebaseUser";
import { firebaseClient } from "firebaseClient";
import { getAccount } from "utils/sessionHelper";
import { roundUp } from "utils/helpers";
import { ModalInfoIcon } from "components/popups/ModalInfoIcon";
import { textStyle } from "theme/names";

interface Props {
  tweet: any;
  isOpen: boolean;
  onClose: () => void;
}

export function StatsPopup({ tweet, isOpen, onClose }: Props) {
  return (
    <Modal isOpen={isOpen} onClose={onClose} returnFocusOnClose={false}>
      <ModalOverlay />
      <ModalContent pb="3">
        <ModalHeader>
          {/* <ModalInfoIcon /> */}
          Statistics for this post
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <StatsPopupBody tweet={tweet} />
        </ModalBody>

        <ModalFooter justifyContent="center">
          <NextLink href="/stats" passHref>
            <Button
              mb={3}
              mt={1}
              as={Link}
              variant="primaryWarning"
              // colorScheme="yellow"
              // color="white"
              // borderRadius="3xl"
              leftIcon={<ImStatsBars />}
              // _hover={{
              //   textDecor: "none",
              // }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              Analytics
            </Button>
          </NextLink>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

interface StatsPopupBodyProps {
  tweet: any;
}

function StatsPopupBody({ tweet }: StatsPopupBodyProps) {
  const [stats, setStats] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasFetched, setHasFetched] = useState<boolean>(false);
  const isAuthenticated = useFirebaseUser();
  const { data: session } = useSession() ?? {}
  const threadTweets = tweet.text.split("\n\n\n");

  const getStats = async () => {
    try {
      setIsLoading(true);
      const db = firebaseClient.firestore();

      // let id = tweet.id_str?.replace("urn:li:share:", "urn:li:activity:"); // not working, the id is different
      const doc = await db.collection("linkedinPosts").doc(tweet.id_str).get();

      setStats(doc.data());
      setIsLoading(false);
    } catch (err) {
      setIsLoading(false);
      console.log("There was a problem in loading stats: ", err);
      toast.error("There was a problem in loading stats: " + err.message);
    }
  };

  useEffect(() => {
    if (isAuthenticated && !hasFetched) {
      setHasFetched(true);
      getStats();
    }
  }, [isAuthenticated]);

  if (!threadTweets) {
    return null;
  }

  return (
    <Box>
      <Flex justifyContent="center">
        <Thread
          threadTweets={threadTweets}
          disableNbChar={true}
          tweet={tweet}
          showOutline={true}
          disablePreview={tweet?.isPreventLinkExpand}
        />
      </Flex>
      {isLoading ? (
        <Center pt={10}>
          <PuffLoader
            color={"#09527c"}
            loading={isLoading}
            cssOverride={{display: "block", margin: "0 auto", borderColor: "red"}}
            size={50}
          />
        </Center>
      ) : stats ? (
        <StatsUI stats={stats} />
      ) : (
        getAccount(session)?.idLinkedin ? (
            <Text textStyle={textStyle["body.medium.light"]} textAlign="center" mt="5">
              <i>No stats found.</i> 😔
            </Text>
          ) : (
            <Center>
              <Link textDecoration="underline" textAlign="center" mt="5" href="/connect">
                Download the Taplio Chrome Extension to collect stats
              </Link>
            </Center>
          )
      )}
    </Box>
  );
}

interface StatsUIProps {
  stats: any;
}

function StatsUI({ stats }: StatsUIProps) {
  const engagementPercent = (stats.totalEngagement * 100) / stats.numViews;

  return (
    <Box my="5" fontWeight="bold">
      <Flex justifyContent="space-evenly">
        <Box>
          <Text textStyle={textStyle["body.medium.light"]} fontSize="lg">{roundUp(stats.numViews)}</Text>
          <Text textStyle={textStyle["body.medium.light"]} color="gray">VIEWS</Text>
        </Box>
        <Box>
          <Text textStyle={textStyle["body.medium.light"]} fontSize="lg">{engagementPercent.toFixed(2)}%</Text>
          <Text textStyle={textStyle["body.medium.light"]} color="gray">ENGAGEMENT (%)</Text>
        </Box>
      </Flex>
      <Flex justifyContent="space-evenly" mt="8">
        <Box>
          <Text textStyle={textStyle["body.medium.light"]} fontSize="lg">{roundUp(stats.numLikes)}</Text>
          <Text textStyle={textStyle["body.medium.light"]} color="gray">LIKES</Text>
        </Box>
        <Box>
          <Text textStyle={textStyle["body.medium.light"]} fontSize="lg">{roundUp(stats.numComments)}</Text>
          <Text textStyle={textStyle["body.medium.light"]} color="gray">REPLIES</Text>
        </Box>
        <Box>
          <Text textStyle={textStyle["body.medium.light"]} fontSize="lg">{roundUp(stats.totalShares)}</Text>
          <Text textStyle={textStyle["body.medium.light"]} color="gray">
            SHARES
          </Text>
        </Box>
      </Flex>
    </Box>
  );
}
